import React, { useId, useState } from 'react';
import { OCCUPATION_OPTIONS } from '../constants/occupationOptions';

const inputStyle = {
  width: '100%',
  padding: '0.6rem 0.8rem',
  borderRadius: '0.5rem',
  border: '1.5px solid #fcd34d',
  outline: 'none',
  fontSize: '0.9rem',
  color: '#78350f',
  background: '#fffbeb',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

/**
 * One-time collection for users whose `registered_users.occupation` is still empty
 * (legacy OTP-only rows, or new accounts that skipped it during profile creation).
 * Blocks the app until a valid value is saved (optional “Later” for this session only).
 */
export default function OccupationPromptModal({ onSave, onDefer }) {
  const [value, setValue] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const id = useId();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!value) {
      setErr('Please select your occupation');
      return;
    }
    setLoading(true);
    const { error } = await onSave(value);
    setLoading(false);
    if (error) setErr(error.message || 'Could not save. Try again.');
  };

  return (
    <div
      className="ks-modal-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 20050,
        background: 'rgba(0,0,0,.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="occ-prompt-title"
        style={{
          width: 'min(420px, 100%)',
          borderRadius: '16px',
          background: 'linear-gradient(180deg,#fff7ed,#fffbeb)',
          border: '1px solid #fde68a',
          boxShadow: '0 16px 48px rgba(0,0,0,.15)',
          padding: '1.35rem 1.25rem 1.1rem',
          fontFamily: 'system-ui,-apple-system,sans-serif',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="occ-prompt-title" style={{ fontSize: '1.1rem', fontWeight: 800, color: '#78350f', margin: '0 0 0.35rem' }}>
          Occupation
        </h2>
        <p style={{ fontSize: '0.8rem', color: '#92580e', margin: '0 0 0.5rem', lineHeight: 1.5 }}>
          Please select your occupation to continue.
        </p>
        <form onSubmit={handleSubmit}>
          {err ? (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '0.5rem',
                padding: '0.5rem 0.75rem',
                marginBottom: '0.65rem',
                fontSize: '0.8rem',
                color: '#dc2626',
              }}
            >
              {err}
            </div>
          ) : null}
          <label htmlFor={id} style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#92400e', marginBottom: '0.35rem' }}>
            Occupation <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <select
            id={id}
            value={value}
            required
            onChange={(e) => setValue(e.target.value)}
            style={inputStyle}
            onFocus={(e) => { e.target.style.borderColor = '#f59e0b'; }}
            onBlur={(e) => { e.target.style.borderColor = '#fcd34d'; }}
          >
            <option value="">— Select —</option>
            {OCCUPATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <p style={{ fontSize: '0.7rem', color: '#a16207', margin: '0.5rem 0 0' }}>
            This helps us personalise your insights better
          </p>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              marginTop: '0.9rem',
              padding: '0.72rem',
              borderRadius: '0.6rem',
              border: 'none',
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              background: 'linear-gradient(135deg,#78350f,#b45309)',
              color: '#fffbeb',
            }}
          >
            {loading ? 'Saving…' : 'Save and continue →'}
          </button>
        </form>
        {typeof onDefer === 'function' ? (
          <button
            type="button"
            onClick={onDefer}
            style={{
              width: '100%',
              marginTop: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: '#b45309',
              fontSize: '0.75rem',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Remind me next time
          </button>
        ) : null}
      </div>
    </div>
  );
}
